import React, {useEffect, useMemo, useState} from "react"
import PropTypes from "prop-types"
import dayjs from "dayjs"
import SearchRoundedIcon from "@mui/icons-material/SearchRounded"
import DateRangeRoundedIcon from "@mui/icons-material/DateRangeRounded"
import {Box, Grid, Paper, Stack, Typography} from "@mui/material"
import {IntegrationService} from "src/services/integration-service"
import ButtonWithIcon from "src/v2/components/Button/Button"
import DatePickerSelectV2 from "src/v2/components/Date/date-picker-select"
import SelectComponent from "src/v2/components/select/select-component"
import {dispatchSnackbar} from "src/v2/components/SnackBar/snackbar-bus"
import {
    buildReportDataTable,
    decodeResponsePayload,
    REPORT_ROUTINE_OPTIONS,
} from "../report-data"
import ReportTable from "./report-table"

const ReportDataTab = ({onLoadingChange}) => {
    const service = useMemo(() => new IntegrationService(), [])
    const [routine, setRoutine] = useState("")
    const [date, setDate] = useState(dayjs().format("YYYY-MM-DD"))
    const [loading, setLoading] = useState(false)
    const [table, setTable] = useState({rows: [], columns: []})

    useEffect(() => () => onLoadingChange(false), [onLoadingChange])

    const load = async (period = false) => {
        if (!routine || !date) {
            dispatchSnackbar("Selecione uma rotina e uma data", "warning")
            return
        }
        setLoading(true)
        onLoadingChange(true)
        setTable({rows: [], columns: []})
        try {
            const response = period
                ? await service.getReportDataPeriodoInfo(routine, date)
                : await service.getReportDataInfo(routine, date)
            const result = buildReportDataTable(decodeResponsePayload(response))
            setTable(result)
            if (result.rows.length === 0) dispatchSnackbar("Nenhum dado disponível", "warning")
        } catch (error) {
            dispatchSnackbar(error?.message || "Erro ao buscar ou processar o relatório", "error")
        } finally {
            setLoading(false)
            onLoadingChange(false)
        }
    }

    return (
        <Stack spacing={3} sx={{pt: 3}}>
            <Paper sx={{p: 3, backgroundColor: "background.paper"}}>

                <BoxHeader/>
                <Box sx={{mt: 4}}></Box>
                <Grid container spacing={2} alignItems="center">
                    <Grid item xs={12} md={5}>
                        <SelectComponent
                            label="Rotina"
                            value={routine}
                            onChange={(event) => setRoutine(event.target.value)}
                            options={REPORT_ROUTINE_OPTIONS}
                        />
                    </Grid>
                    <Grid item xs={12} sm={6} md={2}>
                        <DatePickerSelectV2
                            size={"sm"}
                            label="Data de exibição"
                            value={date}
                            onChange={(value) => {
                                if (value && dayjs(value).isValid()) setDate(dayjs(value).format("YYYY-MM-DD"))
                            }}
                        />
                    </Grid>
                    <Grid item xs={12} sm={6} md={5}>
                        <Stack direction={{xs: "column", sm: "row"}} spacing={1.5}>
                            <ButtonWithIcon icon={<SearchRoundedIcon/>} onClick={() => load(false)} disabled={loading}>
                                CONSULTAR
                            </ButtonWithIcon>
                            <ButtonWithIcon icon={<DateRangeRoundedIcon/>} onClick={() => load(true)}
                                            disabled={loading}>
                                CONSULTAR 15 DIAS
                            </ButtonWithIcon>
                        </Stack>
                    </Grid>
                </Grid>

            </Paper>
            <ReportTable title="Relatório" rows={table.rows} columns={table.columns} loading={loading}/>
        </Stack>
    )
}

const BoxHeader = () => (
    <div>
        <Typography variant="h3" color="common.white">Relatório de dados</Typography>
        <Typography variant="body2" color="primary.white70" sx={{mt: 0.5}}>
            Consulte os resultados registrados por uma rotina e data de referência.
        </Typography>
    </div>
)

ReportDataTab.propTypes = {onLoadingChange: PropTypes.func.isRequired}

export default ReportDataTab
